import { Modal, Table, Tag } from "antd";
import dayjs from "dayjs";
import { useGetMSSchedules } from "queries/meetingSchedule";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SCHEDULE_STATUS } from "../../../scheduleStatus";

const EXCLUDED_STATUS_IDS = [
  SCHEDULE_STATUS.CANCELLED,
  SCHEDULE_STATUS.NO_SHOW,
];

/**
 * Daftar jadwal milik 1 agent (schedule_admin) untuk rentang hari ini −30 hari
 * s/d +14 hari — dipakai dari modal "Alasan pemilihan agent" saat nama agent
 * atau kolom Aktual diklik.
 */
const AgentSchedulesModal = ({
  open,
  onClose,
  scheduleAdminId,
  agentName,
  startDate,
  endDate,
  title,
}) => {
  const { t } = useTranslation();

  const range = useMemo(
    () =>
      startDate && endDate
        ? { start_date: startDate, end_date: endDate }
        : {
            start_date: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
            end_date: dayjs().add(14, "day").format("YYYY-MM-DD"),
          },
    [startDate, endDate]
  );

  const { data, isLoading } = useGetMSSchedules(
    {
      schedule_admin_id: scheduleAdminId,
      start_date: range.start_date,
      end_date: range.end_date,
      per_page: 1000,
    },
    { enabled: open && Boolean(scheduleAdminId) }
  );

  const rows = Array.isArray(data?.data) ? data.data : [];
  const filteredRows = rows.filter(
    (r) => !EXCLUDED_STATUS_IDS.includes(Number(r.status_id))
  );
  const total = filteredRows.length;

  const columns = [
    {
      title: "No",
      key: "no",
      width: 52,
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: t("meeting_schedule.agent_col_date"),
      dataIndex: "date",
      key: "date",
      width: 110,
      render: (v) => (v ? dayjs(v).format("DD MMM YYYY") : "—"),
    },
    {
      title: t("date.time"),
      key: "time",
      width: 120,
      render: (_, r) =>
        r.start_time && r.end_time
          ? `${dayjs(r.start_time, "HH:mm:ss").format("HH:mm")} – ${dayjs(
              r.end_time,
              "HH:mm:ss"
            ).format("HH:mm")}`
          : "—",
    },
    {
      title: t("name"),
      dataIndex: "contact",
      key: "contact",
      render: (v) => v || "—",
    },
    {
      title: t("company"),
      dataIndex: "company",
      key: "company",
      render: (v) => v || "—",
    },
    {
      title: t("meeting_management.status"),
      key: "status",
      render: (_, r) =>
        r.status_name ? (
          <Tag color={r.status_color} className="m-0 !text-black">
            {r.status_name}
          </Tag>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      title={
        title || t("meeting_schedule.agent_schedules_title", { name: agentName })
      }
      destroyOnHidden
    >
      <Table
        size="small"
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={filteredRows}
        pagination={false}
        scroll={{ y: 420 }}
      />
      <div className="mt-3 text-right text-sm font-medium text-neutral-700">
        {t("meeting_schedule.agent_schedules_total", { count: total })}
      </div>
    </Modal>
  );
};

export default AgentSchedulesModal;
